// Home's Req health widget (DC-17, DC-49; CR-046 "Health = numeric + traffic light + drivers."): the mean score in the
// ring, the three bands as counts that drill to the Portfolio filtered by band, then the requisitions that most need
// attention (Red first, at most six, as S13 sends them), each with its number in the ring, its band word and its drivers
// as deep-linked rows. His foot line closes it: "Health informs attention; it does not make employment decisions." A
// requisition is scored; no person is, anywhere.

import {
  CheckRow, Checklist, Flex, Health, Paragraph, Pill, Ring, Row, RowName, Stack, TextLink, Widget, type CheckStatus, type HealthDriver,
} from "@/mc";
import { RULE_LABELS } from "@/mc/labels";
import { METRIC_LABELS } from "@/mc/g3/labels";
import { SURFACE_LABELS } from "@/mc/core/labels";
import { reportHref } from "@/views/b10/reports/spec";
import type { HomeWidgetView } from "./host";
import { DASH, reqHealthOf, seatsOf, type Light, type ReqHealthData, type ReqHealthRow } from "./api";
import { BAND_ORDER, BAND_WORDS, DRIVER_WORDS, HOME_TEMPLATES, type Band } from "./labels";
import { hsKey } from "@/views/b10/lib/hsKey";

const BAND_TONE = { red: "red", yellow: "warn", green: "ok" } as const;
const DRIVER_STATUS: Readonly<Record<Light, CheckStatus>> = { red: "bad", yellow: "warn", green: "ok" };

/** The Portfolio's requisitions in one band: the drill-down of a band count (dimension `health`, plan D5). */
export const bandHref = (band: Band) => reportHref("portfolio", { filters: { health: [band] } });

function Bands({ data }: { data: ReqHealthData }) {
  return (
    <Flex gap={12} wrap>
      {data.mean !== null ? <Ring size={44} n={data.mean} label={HOME_TEMPLATES.reqHealthMean({ n: Math.round(data.mean) })} /> : null}
      {BAND_ORDER.map((band) => (
        <TextLink key={band} href={bandHref(band)} hs={hsKey("dash.health.band", band)}>
          <Pill tone={BAND_TONE[band]}>{BAND_WORDS[band]}</Pill>
          {" "}
          {data.bands[band]}
        </TextLink>
      ))}
    </Flex>
  );
}

/** A requisition's drivers as D0 Health drivers: his driver word, the band's status, the deep link that clears it. */
export function driversOf(row: ReqHealthRow): HealthDriver[] {
  return row.drivers.map((driver) => ({
    status: row.light ? DRIVER_STATUS[row.light] : "todo",
    text: DRIVER_WORDS[driver.key] ?? SURFACE_LABELS.drivers,
    href: driver.route || undefined,
    hs: hsKey("dash.health.driver", row.requisition_id, driver.key),
  }));
}

export function HealthRow({ row }: { row: ReqHealthRow }) {
  const drivers = driversOf(row);
  return (
    <Stack gap={4}>
      <Row cols={2} lead="70%" href={row.route || undefined} hs={hsKey("dash.health.req", row.requisition_id)}>
        <RowName name={row.title} sub={row.requisition_id} />
        {row.score !== null && row.light !== null ? <Health size="sm" score={row.score} band={row.light} drivers={drivers} /> : null}
      </Row>
      {drivers.length > 0 ? (
        <Checklist>
          {drivers.map((driver, index) => (
            <CheckRow key={row.drivers[index]?.key ?? index} status={driver.status} href={driver.href} hs={driver.hs}>{driver.text}</CheckRow>
          ))}
        </Checklist>
      ) : null}
    </Stack>
  );
}

export function ReqHealthBody({ data }: { data: ReqHealthData }) {
  return (
    <Stack gap={12}>
      <Bands data={data} />
      {data.rows.map((row) => <HealthRow key={row.requisition_id} row={row} />)}
      <Paragraph sm>{RULE_LABELS.healthInformsAttention}</Paragraph>
    </Stack>
  );
}

// D-25 (UAT3 floor ruling 05:42): a widget home.open composes is drawn even with nothing in it, its Card's empty
// slot holding B10's DASH (DS §2.1 empty → EmptyState; PLAN D2 "nothing measured" is "—"), until he words the line
// (Q-E19 "What should the empty-state line say?").
/** Widget `req_health` (S13 → S16, frozen). */
export const ReqHealth: HomeWidgetView = ({ widgetKey, data }) => {
  const health = reqHealthOf(data);
  const counted = health.bands.green + health.bands.yellow + health.bands.red;
  const none = health.mean === null && counted === 0 && health.rows.length === 0;
  return (
    <Widget id={widgetKey} kind="health" roles={seatsOf(widgetKey)} title={METRIC_LABELS.reqHealth} count={counted} empty={none ? DASH : undefined}>
      {none ? null : <ReqHealthBody data={health} />}
    </Widget>
  );
};
